import CanonicalTags from "@/components/seo/CanonicalTags";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { CTA_TITLE_LG, CtaBand, WaitlistCta } from "@/components/marketing/parts";

const CREW = [
  {
    name: "Crew",
    role: "Engineering",
    body: "Builds and maintains Compaire, including Fit Scores and the features agents use.",
  },
  {
    name: "Faith",
    role: "Product",
    body: "Supports work on Fit Scores and comparisons, helping agents understand their options.",
  },
  {
    name: "Leanne",
    role: "Support",
    body: "Helps agents and brokerages get set up, get unstuck, and get the most from Compaire.",
  },
];

const BELIEFS = [
  {
    tag: "Private",
    title: "Explore without exposure.",
    body: "Compare teams and brokerages privately. No one sees you looking until you decide they should.",
  },
  {
    tag: "Transparent",
    title: "Know what to ask.",
    body: "Use company stories, comparisons and reviews to learn about a team or brokerage, then ask about the details that matter to you.",
  },
  {
    tag: "Your long-term future",
    title: "Choose with your future in\u00a0mind.",
    body: "Where you work can shape your career for years. Take time to understand your options before deciding.",
  },
];

const AboutPage = () => (
  <MarketingLayout>
    <CanonicalTags
      path="/about"
      title="About Compaire | Meet the People Behind It"
      description="Meet Jesse Garcia and Joe Duenat, the people behind Compaire, bringing decades of experience in real estate and technology."
    />

    <section style={{ padding: "clamp(64px,10cqi,144px) 0 clamp(64px,8cqi,112px)" }}>
      <div className="mk-container mk-stack" style={{ gap: 28 }}>
        <div className="mk-label">Why we built Compaire</div>
        <h1 className="mk-h1" style={{ maxWidth: 1000 }}>
          The biggest decision of a career, made in the dark.
        </h1>
        <p className="mk-lede" style={{ marginTop: 12, maxWidth: 720 }}>
          Choosing where to hang your license shapes your income, your daily work and your life outside work. Compaire
          brings company stories, comparisons and conversations into one place, so you can get to know an organization
          before making a move.
        </p>
      </div>
    </section>

    <section style={{ background: "var(--mk-primary)", color: "#fff" }}>
      <div
        className="mk-container"
        style={{
          padding: "clamp(48px,6cqi,72px) var(--mk-gutter)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "12px 48px",
        }}
      >
        <span className="mk-label mk-label--onteal">Explore your options</span>
        <p
          style={{
            flex: "1 1 560px",
            maxWidth: 820,
            font: "600 var(--mk-text-h2)/1.08 var(--mk-sans)",
            color: "#fff",
            textWrap: "balance",
          }}
        >
          Look before anyone knows you're looking.
        </p>
      </div>
    </section>

    <section className="mk-section mk-section--lg mk-section--white">
      <div
        className="mk-container"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,380px),1fr))",
          gap: "48px clamp(48px,7cqi,112px)",
          alignItems: "start",
        }}
      >
        <img
          src="/about/jesse-garcia.jpg"
          alt="Jesse Garcia, Founder and CEO of Compaire"
          width={1024}
          height={1024}
          loading="lazy"
          style={{ width: "100%", height: "auto", display: "block", borderRadius: 8, background: "var(--mk-tint)" }}
        />
        <div className="mk-stack" style={{ gap: 22 }}>
          <div className="mk-label">Founder</div>
          <div className="mk-stack" style={{ gap: 8 }}>
            <h2 style={{ font: "600 var(--mk-text-h2-lead)/1.02 var(--mk-sans)" }}>Jesse Garcia</h2>
            <span style={{ font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-ink-2)" }}>Founder &amp; CEO</span>
          </div>
          {/* Jesse's prior work (his earlier companies), not Compaire's traction. */}
          <p style={{ font: "400 var(--mk-text-body-lg)/1.55 var(--mk-sans)", textWrap: "pretty" }}>
            Jesse brings more than 20 years of experience in real estate and technology as a founder and operator. He
            has built and led real estate organizations across market cycles, with experience in brokerage operations,
            recruiting, agent retention and brokerage economics. He also co-founded Zipi, a financial infrastructure
            platform for brokerages acquired by SkySlope.
          </p>
          <p style={{ font: "400 var(--mk-text-body-lg)/1.55 var(--mk-sans)", textWrap: "pretty" }}>
            At Compaire, Jesse helps agents make more informed career decisions and helps teams and brokerages explain
            what makes them a good fit. He knows how culture, leadership and support shape an agent’s everyday
            experience&#8288;—and why those details matter before a move.
          </p>
        </div>
      </div>
      <div
        className="mk-container"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,380px),1fr))",
          gap: "48px clamp(48px,7cqi,112px)",
          alignItems: "start",
        }}
      >
        <div
          style={{
            gridColumn: "1 / -1",
            marginTop: "clamp(56px,7cqi,96px)",
            borderTop: "1px solid var(--mk-border)",
          }}
          aria-hidden="true"
        />
        <picture>
          <source
            type="image/webp"
            srcSet="/about/joe-duenat-640.webp 640w, /about/joe-duenat-1024.webp 1024w"
            sizes="(min-width: 1040px) 520px, 100vw"
          />
          <img
            src="/about/joe-duenat.jpg"
            alt="Joe Duenat, Head of Product & Growth at Compaire"
            width={1024}
            height={1024}
            loading="lazy"
            decoding="async"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              borderRadius: 8,
              background: "var(--mk-tint)",
            }}
          />
        </picture>
        <div className="mk-stack" style={{ gap: 22 }}>
          <div className="mk-label">Product &amp; Growth</div>
          <div className="mk-stack" style={{ gap: 8 }}>
            <h2 style={{ font: "600 var(--mk-text-h2-lead)/1.02 var(--mk-sans)" }}>Joe Duenat</h2>
            <span style={{ font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-ink-2)" }}>
              Head of Product &amp; Growth
            </span>
          </div>
          {/* Joe's prior work (his earlier companies), not Compaire's traction. */}
          <p style={{ font: "400 var(--mk-text-body-lg)/1.55 var(--mk-sans)", textWrap: "pretty" }}>
            Joe brings more than 25 years of experience in real estate marketing and technology as a founder
            and operator. He co-founded Curated Social, a content platform for real estate agents that was acquired by
            RateMyAgent.
          </p>
          <p style={{ font: "400 var(--mk-text-body-lg)/1.55 var(--mk-sans)", textWrap: "pretty" }}>
            At Compaire, Joe leads product and growth, helping teams and brokerages tell their stories and making it
            easier for agents to understand their options. His experience building subscription businesses and
            marketing to real estate professionals helps keep the product focused on the people using it&#8288;—and on
            making that first conversation more useful for both sides.
          </p>
        </div>
      </div>
    </section>

    <section className="mk-section mk-section--lg">
      <div className="mk-container mk-stack" style={{ gap: "clamp(40px,5cqi,64px)" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "20px 64px",
          }}
        >
          <div className="mk-stack" style={{ flex: "1 1 480px", gap: 20 }}>
            <div className="mk-label">How we operate</div>
            <h2 className="mk-h2 mk-h2--feature">Built by people who know the work.</h2>
          </div>
          <p className="mk-body-lg" style={{ flex: "1 1 360px", maxWidth: 520, lineHeight: 1.6 }}>
            Jesse Garcia and Joe Duenat bring decades of experience in real estate and technology. They guide Compaire's
            product and growth, with AI tools supporting engineering, product work and support.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,280px),1fr))",
            gap: 1,
            background: "var(--mk-border)",
            borderTop: "1px solid var(--mk-border)",
            borderBottom: "1px solid var(--mk-border)",
          }}
        >
          {CREW.map((member, i) => (
            <div
              key={member.name}
              className="mk-stack"
              style={{
                background: "var(--mk-paper)",
                padding:
                  i === 0
                    ? "32px clamp(0px,2cqi,28px) 32px 0"
                    : i === CREW.length - 1
                      ? "32px 0 32px clamp(0px,2cqi,28px)"
                      : "32px clamp(0px,2cqi,28px)",
                gap: 14,
              }}
            >
              <span style={{ font: "400 var(--mk-text-meta)/1.3 var(--mk-mono)", color: "var(--mk-muted)" }}>AI System</span>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                <h3 style={{ font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)" }}>{member.name}</h3>
                <span style={{ font: "500 var(--mk-text-label-sm)/1.3 var(--mk-sans)", color: "var(--mk-primary)" }}>{member.role}</span>
              </div>
              <p className="mk-body">{member.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="mk-section mk-section--lg mk-section--white mk-section--rule">
      <div className="mk-container mk-stack" style={{ gap: 40 }}>
        <h2 className="mk-label">What we believe</h2>
        <div className="mk-stack" style={{ borderTop: "1px solid var(--mk-ink)" }}>
          {BELIEFS.map((belief) => (
            <div
              key={belief.tag}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px clamp(32px,5cqi,80px)",
                padding: "36px 0",
                borderBottom: "1px solid var(--mk-border)",
              }}
            >
              <span style={{ flex: "0 0 200px", font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-primary)" }}>
                {belief.tag}
              </span>
              <h3 style={{ flex: "1 1 340px", font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)", textWrap: "balance" }}>
                {belief.title}
              </h3>
              <p className="mk-body" style={{ flex: "1 1 300px" }}>
                {belief.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <CtaBand
      eyebrow="Early access"
      title="Help shape Compaire."
      body="Join the first group of agents, team leaders and brokerage owners using Compaire. Explore your options, share your feedback and help us make it more useful."
      padding="clamp(88px,11cqi,152px) var(--mk-gutter)"
      titleStyle={CTA_TITLE_LG}
      cta={
        <WaitlistCta variant="inverse" size="lg" style={{ padding: "0 26px" }}>
          Request early access →
        </WaitlistCta>
      }
    />
  </MarketingLayout>
);

export default AboutPage;
